import { Stack, Text, Group, RangeSlider, Box, Switch } from '@mantine/core';
import dayjs from 'dayjs';
import {
  isDayClosed,
  LAST_MINUTE_OF_DAY,
  timeToNumber,
  numberToTime,
  formatTimeLabel,
  getHourMarks,
  getHourLabels,
  resolvePrimaryId,
} from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/helpers';

import CalendarSelect from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/CalendarSelect';
import EditScheduleMenu from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/EditScheduleMenu';

interface HolidaySchedule {
  appliedDate: string;
  dayIndex?: string;
  endTime: string;
  startTime: string;
  tempId?: string;
  uuid: string;
}

interface HolidayTimelineProps {
  schedules: HolidaySchedule[];
  handleToggle: (id: string) => void;
  handle24Hours: (id: string) => void;
  handleDeleteHours: (id: string) => void;
  handleChange: (val: string, id: string, type: string) => void;
  handleTimeRangeChange: (id: string, times: [string, string]) => void;
}

export default function HolidayTimeline({
  schedules,
  handleToggle,
  handle24Hours,
  handleDeleteHours,
  handleChange,
  handleTimeRangeChange,
}: HolidayTimelineProps) {
  const handleTimeChange = (values: [number, number], schedule: HolidaySchedule) => {
    const scheduleId = resolvePrimaryId({ ...schedule, dayIndex: schedule.dayIndex ?? '' });
    if (!scheduleId) return;

    const [startValue, endValue] = values;
    handleTimeRangeChange(scheduleId, [numberToTime(startValue), numberToTime(endValue)]);
  };

  const hourMarks = getHourMarks();
  const hourLabels = getHourLabels();

  return (
    <Stack gap="md">
      {/* Hour labels at the top */}
      <Group align="center">
        <Box pos="relative" flex={1} h={20} ml={234}>
          {hourLabels.map(({ hour, displayHour }) => {
            const position = ((hour * 60) / 1440) * 100; // 1440 is total minutes in a day
            return (
              <Text
                key={`hour-label-${hour}`}
                variant="caption-xs2"
                left={`${position}%`}
                w="nowrap"
                pos="absolute"
              >
                {displayHour}
              </Text>
            );
          })}
        </Box>
        <Box w={40} />
      </Group>

      {/* Schedule timeline */}
      {schedules.map(schedule => {
        const scheduleForHelpers = { ...schedule, dayIndex: schedule.dayIndex ?? '' };
        const scheduleId = resolvePrimaryId(scheduleForHelpers);
        if (!scheduleId) return null;

        const isClosed = isDayClosed(scheduleForHelpers);

        return (
          <Group key={scheduleId} align="flex-start" w="100%" wrap="nowrap">
            <Group gap="xs2" w={210} align="flex-start" wrap="nowrap">
              <Switch
                checked={!isClosed}
                onChange={() => handleToggle(scheduleId)}
                label={
                  <CalendarSelect
                    value={dayjs(schedule.appliedDate).format('MM/DD/YYYY')}
                    onSelectNewDay={v =>
                      handleChange(dayjs(v).format('YYYY-MM-DD'), scheduleId, 'appliedDate')
                    }
                  />
                }
                size="xs"
              />
            </Group>
            <Stack flex={1} gap={4}>
              {isClosed && (
                <Box
                  h="var(--dimension-lg)"
                  bg="var(--surface-secondary)"
                  display="flex"
                  style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text variant="caption-xs">CLOSED</Text>
                </Box>
              )}
              {!isClosed && (
                <Group align="center" wrap="nowrap">
                  <Box flex={1} ml="var(--spacing-md)">
                    <RangeSlider
                      color="var(--surface-paper-2)"
                      min={0}
                      max={LAST_MINUTE_OF_DAY}
                      step={15}
                      minRange={15}
                      value={[timeToNumber(schedule.startTime), timeToNumber(schedule.endTime)]}
                      onChange={values => handleTimeChange(values, schedule)}
                      label={formatTimeLabel}
                      labelAlwaysOn
                      marks={hourMarks}
                    />
                  </Box>
                </Group>
              )}
            </Stack>
            <Box w={32} pt={5}>
              <EditScheduleMenu
                schedule={schedule}
                schedules={schedules}
                handle24Hours={() => handle24Hours(scheduleId)}
                handleDeleteHours={() => handleDeleteHours(scheduleId)}
                isSpecial
              />
            </Box>
          </Group>
        );
      })}
    </Stack>
  );
}
